<template>
  <DetailsAccordion
    :error-description="i18n(translations.errorDescription)"
    :status
    @refresh="refresh"
  >
    <template #icon>
      <IconTools />
    </template>
    <template #title>
      {{ i18n(translations.title) }}
    </template>
    <template #description>
      <span
        v-if="events.length > 0"
        data-allow-mismatch="text"
        data-test="description"
      >
        {{ i18n.date(events[0].date) }} {{ events[0].label }}
      </span>
      <span v-else>{{ i18n(translations.noEventDescription) }}</span>
    </template>
    <template #body>
      <ul
        v-if="events.length > 0"
        class="divide-static-default-low space-y-24 divide-y"
        data-test="events-list"
      >
        <li
          v-for="event in events"
          :key="event.id"
          class="pt-24 first:pt-0 only:pt-0"
        >
          <p class="mb-16">
            {{ i18n.date(event.date) }}
          </p>

          <div class="grow space-y-8">
            <p>{{ event.label }}</p>
          </div>
        </li>
      </ul>
      <span v-else>{{ i18n(translations.noEventBody) }}</span>
    </template>
  </DetailsAccordion>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { IconTools } from '@ds/icons/IconTools'

import { useOrderlineDetails } from '~/scopes/customer-care/pages/request/composables/useOrderlineDetails'

import DetailsAccordion from '../DetailsAccordion/DetailsAccordion.vue'

import translations from './Events.translations'

const i18n = useI18n()

const {
  events: { data: contacts, status, refresh },
} = useOrderlineDetails()

type Event = {
  id: string
  date: Date
  label: string
}

function normalizeIssueString(issue: string) {
  return issue.charAt(0) + issue.substring(1).toLowerCase().replaceAll('_', ' ')
}

const events = computed(() => {
  if (!contacts.value || contacts.value.length === 0) return []

  const contactsEvents = contacts.value.reduce<Event[]>(
    (allEvents: Event[], contact) => {
      return contact.issues.length
        ? [
            ...allEvents,
            {
              id: contact.id.toString(),
              date: new Date(contact.createdAt),
              // Concatenate every issues declared for one contact
              label: contact.issues
                .map(({ name }) => normalizeIssueString(name))
                .join(' + '),
            },
          ]
        : allEvents
    },
    [],
  )

  return contactsEvents.sort((a, b) => {
    // Compare the dates to sort in anti-chronological order
    return b.date.getTime() - a.date.getTime()
  })
})
</script>
